import { useState, useRef, useEffect } from "react";
import { Mode, ModeId } from "../types/Mode";

interface ModeSelectorProps {
  modes: Mode[];
  value: ModeId;
  onChange: (value: ModeId) => void;
  disabled?: boolean;
  isBottom?: boolean;
}

export function ModeSelector({
  modes,
  value,
  onChange,
  disabled = false,
  isBottom = false,
}: ModeSelectorProps) {
  const [isModeMenuOpen, setIsModeMenuOpen] = useState(false);
  const modeMenuButtonRef = useRef<HTMLButtonElement>(null);

  // Close mode menu when clicking outside
  useEffect(() => {
    if (!isModeMenuOpen) {
      return () => {
        // No cleanup needed when menu is closed
      };
    }

    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      if (
        modeMenuButtonRef.current &&
        !modeMenuButtonRef.current.contains(target) &&
        !(target instanceof Element && target.closest("[data-mode-menu]"))
      ) {
        setIsModeMenuOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isModeMenuOpen]);

  const currentMode = modes.find((m) => m.id === value) || modes[0];

  const handleModeSelect = (selectedMode: ModeId) => {
    if (!disabled) {
      onChange(selectedMode);
      setIsModeMenuOpen(false);
    }
  };

  const Icon = currentMode.icon;

  return (
    <div className="relative min-w-0 flex-shrink">
      <button
        ref={modeMenuButtonRef}
        type="button"
        tabIndex={0}
        onClick={() => setIsModeMenuOpen(!isModeMenuOpen)}
        disabled={disabled}
        className="btn btn-ghost text-base-content/70 hover:bg-base-200 h-7 px-3 normal-case gap-1.5 min-w-0 max-w-full hover:border-base-200 disabled:opacity-50 disabled:cursor-not-allowed"
        aria-label="Sélectionner un mode"
      >
        <Icon className="w-3 h-3 sm:w-4 sm:h-4 text-base-content/70 flex-shrink-0" />
        <span className="text-xs sm:text-sm truncate min-w-0">{currentMode.label}</span>
      </button>
      {isModeMenuOpen && (
        <ul
          data-mode-menu
          tabIndex={0}
          className={`dropdown-content menu bg-white rounded-box z-20 w-64 p-2 shadow absolute left-0 overflow-visible ${
            isBottom ? "bottom-full mb-2" : "top-full mt-2"
          }`}
        >
          {modes.map((mode) => (
            <li
              key={mode.id}
              className="overflow-visible"
              onClick={() => handleModeSelect(mode.id)}
            >
              <button
                className={`flex w-full items-center gap-2 text-sm cursor-pointer ${
                  value === mode.id ? "active font-semibold" : ""
                }`}
                disabled={disabled}
              >
                <mode.icon className="w-4 h-4 text-base-content/70" />
                <span>{mode.label}</span>
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
